<script setup lang="ts">
import {
  DialogRoot,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from 'reka-ui'
import { Button } from '@/lib/ui/button'

interface IConfirmationDialogProps {
  readonly open: boolean
  readonly title: string
  readonly description?: string
  readonly confirmLabel?: string
  readonly cancelLabel?: string
  readonly destructive?: boolean
}

interface IConfirmationDialogEmits {
  'update:open': [value: boolean]
  confirm: []
  cancel: []
}

withDefaults(defineProps<IConfirmationDialogProps>(), {
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  destructive: false,
})
const emit = defineEmits<IConfirmationDialogEmits>()

function handleConfirm() {
  emit('confirm')
  emit('update:open', false)
}

function handleCancel() {
  emit('cancel')
  emit('update:open', false)
}
</script>

<template>
  <!--
    Intentionally not using DialogPortal — keeping the dialog in the .siddchatbot
    DOM tree so CSS custom properties resolve correctly, and using fixed positioning
    so overflow-hidden ancestors don't clip it.
  -->
  <DialogRoot :open="open" @update:open="emit('update:open', $event)">
    <DialogOverlay
      class="fixed inset-0 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      style="z-index: calc(var(--widget-z-index) + 3)"
    />
    <DialogContent
      class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-background border rounded-lg p-6 shadow-lg flex flex-col gap-4 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
      style="z-index: calc(var(--widget-z-index) + 4)"
    >
      <div class="flex flex-col gap-1.5">
        <DialogTitle class="text-base font-semibold text-foreground">{{
          title
        }}</DialogTitle>
        <DialogDescription
          v-if="description"
          class="text-sm text-muted-foreground"
        >
          {{ description }}
        </DialogDescription>
      </div>

      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" @click="handleCancel">{{ cancelLabel }}</Button>
        <Button
          :variant="destructive ? 'destructive' : 'default'"
          :style="
            destructive
              ? {
                  backgroundColor: 'hsl(var(--destructive))',
                  color: 'hsl(var(--destructive-contrast))',
                }
              : undefined
          "
          @click="handleConfirm"
        >
          {{ confirmLabel }}
        </Button>
      </div>
    </DialogContent>
  </DialogRoot>
</template>
